import type { CardType } from '@pushflow/types'
import { useState } from 'react'
import plusButton from '../../../assets/images/buttons/plus-button.svg'
import type { GameDataType, PlayerType } from '../../../types'
import { Icon } from '../../atoms'
import { TableTab } from './components'
import { ETabBorderType } from './enum'
import * as styles from './index.css'
import type { UnhandledMessageType } from './types'

export const MultiTable = (props: {
  userId: string
  currentTableId: string
  tables: {
    id: string
    password: string | null
  }[]
  onUpdate: (
    tableId: string,
    data: GameDataType,
    userPlayer: PlayerType | null,
  ) => Promise<void>
  onUnhandledUpdate: (tableId: string, data: UnhandledMessageType) => void
  onSelect: (tableId: string) => Promise<void>
  onClickNewTab: () => void
}) => {
  const [tabParamByTable, setTabParamByTable] = useState<
    Record<
      string,
      {
        cards?: CardType[]
        turnSeconds?: number
        turnRemainSeconds?: number
        name: string
        state?: Set<'fold'>
        border?: ETabBorderType
      }
    >
  >({})

  const updateTabParams = (
    tableId: string,
    params: Partial<(typeof tabParamByTable)['string']>,
  ) => {
    setTabParamByTable((old) => ({
      ...old,
      [tableId]: {
        ...(old[tableId] || {}),
        ...params,
      },
    }))
  }

  const setTabBorder = (tableId: string, level: ETabBorderType) => {
    if (
      !(tableId in tabParamByTable) ||
      !tabParamByTable[tableId].border ||
      tabParamByTable[tableId].border < level
    ) {
      updateTabParams(tableId, {
        border: level,
      })
    }
  }

  const getUserPlayerData = (data: GameDataType) => {
    if (!data.players.length) {
      return null
    }
    const userPlayerData = data.players.find(
      (player) => player.userId === props.userId,
    )
    return userPlayerData || null
  }

  const onClick = async (tableId: string) => {
    setTabBorder(tableId, ETabBorderType.DEFAULT)
    await props.onSelect(tableId)
  }

  const onUpdate = async (tableId: string, data: GameDataType) => {
    const userPlayerData = getUserPlayerData(data)

    const userTurnRemainSeconds =
      data.table.turnStartedAt !== null &&
      data.table.playerTurnId === props.userId
        ? Math.floor(
            (Date.now() - new Date(data.table.turnStartedAt).valueOf()) / 1000,
          )
        : undefined
    const newTabParams = {
      name: data.info.name || data.info.code,
      cards: userPlayerData?.cards
        ? (userPlayerData.cards.split(' ') as CardType[])
        : undefined,
      turnSeconds: data.info.turnSeconds,
      turnRemainSeconds:
        userTurnRemainSeconds !== undefined
          ? userTurnRemainSeconds > data.info.turnSeconds
            ? undefined
            : data.info.turnSeconds - userTurnRemainSeconds
          : undefined,
    }
    updateTabParams(tableId, newTabParams)
    await props.onUpdate(tableId, data, userPlayerData)
  }

  const onUnhandledUpdate = (tableId: string, data: UnhandledMessageType) => {
    if (tableId !== props.currentTableId) {
      if (data.type === 'poll') {
        setTabBorder(tableId, ETabBorderType.WARN)
      } else if (data.type === 'action') {
        setTabBorder(tableId, ETabBorderType.ERROR)
      }
    }
    props.onUnhandledUpdate(tableId, data)
  }

  return (
    <div className={styles.tabs}>
      {props.tables.map((table) => (
        <TableTab
          key={table.id}
          tableId={table.id}
          password={table.password}
          type={tabParamByTable[table.id]?.border}
          cards={
            props.tables.length !== 1
              ? tabParamByTable[table.id]?.cards
              : undefined
          }
          name={tabParamByTable[table.id]?.name}
          turnSeconds={tabParamByTable[table.id]?.turnSeconds}
          turnRemainSeconds={
            props.tables.length !== 1
              ? tabParamByTable[table.id]?.turnRemainSeconds
              : undefined
          }
          isHidden={
            props.tables.length !== 1 && table.id === props.currentTableId
          }
          isActive={table.id === props.currentTableId}
          onUpdate={(data) => {
            void onUpdate(table.id, data)
          }}
          onUnhandledUpdate={(data) => onUnhandledUpdate(table.id, data)}
          onClick={() => {
            void onClick(table.id)
          }}
        />
      ))}
      {props.tables.length < 3 ? (
        <Icon
          src={plusButton}
          analytic={{
            name: 'new_table_tab',
          }}
          onClick={props.onClickNewTab}
        />
      ) : null}
    </div>
  )
}
